<script lang="ts">
  import { onDestroy } from "svelte"
  import ColumnHeader from "../../_shared/_modules/column-header"
  import TimeScale from "./_modules/time-scale"
  import TimelineGrid from "./_modules/timeline-grid"
  import SoftSlotRail from "./_modules/soft-slot-rail"
  import RouteConnectorOverlay from "./_modules/route-connector-overlay"
  import EventLayer from "./_modules/event-layer"
  import ExternalBusyIntervalLayer from "./_modules/external-busy-interval-layer"
  import AvailabilityMask from "./_shared/_modules/availability-mask"
  import ColumnLoadingOverlay from "./_modules/column-loading-overlay"
  import { startOfDay } from "../../../../../../../../../_shared/_logic/date-math"
  import { buildSoftSlots } from "./_logic/softSlots"
  import { canMoveCalendarEventByDrag, canResizeCalendarEventByDrag } from "./_logic/eventMovement"
  import {
    computeEventPosition,
    computeResizeTimes,
    minutesFromDate,
    minutesFromPointer,
  } from "../../_shared/_logic/drag-resize"
  import {
    ROUTE_GUTTER_WIDTH_PX,
    ROUTE_SOFT_COLUMN_WIDTH_PX,
    ROUTE_VISUAL_GAP_PX,
  } from "./_shared/_logic/route-constants"
  import { applyRouteVisualGaps } from "./_logic/routeVisualGaps"
  import type {
    BoardActions,
    BoardColumn,
    BoardEvent,
    BoardInteractionConfig,
    BoardLayoutConfig,
    BoardMovePosition,
    BoardMovePreview,
    BoardMoveStartPayload,
    SoftSlotDisplayMode,
    TimeSlot,
  } from "../../../../../../_logic/calendar-types"
  import { newNotification } from "@kontextwerk/web-sdk/core/overlay/notifications"

  type BoardPropertyRef = NonNullable<BoardEvent["property"]>
  type ResizeInteractionMode = "resize-start" | "resize-end"

  type InteractionState = {
    mode: ResizeInteractionMode
    item: BoardEvent
    pointerId: number
    originalStart: Date
    originalEnd: Date
    currentStart: Date
    currentEnd: Date
    currentColumnId: string
  }

  interface Props {
    column: BoardColumn
    timeSlots: TimeSlot[]
    layout: BoardLayoutConfig
    actions: BoardActions
    interaction: BoardInteractionConfig
    now: Date
    showCancelReplan?: boolean
    cancelReplanDisabled?: boolean
    showBodyDivider?: boolean
    readOnly?: boolean
    showEventTooltips?: boolean
    showAppointmentTagTooltip?: boolean
    softSlotDisplayMode?: SoftSlotDisplayMode
    isLoading?: boolean
    hiddenEventId?: string | null
    activeMovePosition?: BoardMovePosition | null
    movePreview?: BoardMovePreview | null
    onMoveStart?: (payload: BoardMoveStartPayload) => void
    allowSlotCreation?: boolean
    allowBlockerCreation?: boolean
    showUpdateSuccessNotification?: boolean
    showHeader?: boolean
    formatDayHeader: (day: Date) => string
    formatAddress: (property?: BoardPropertyRef) => string
  }

  let {
    column,
    timeSlots = [],
    layout,
    actions,
    interaction: interactionConfig,
    now = new Date(),
    showCancelReplan = false,
    cancelReplanDisabled = false,
    showBodyDivider = false,
    readOnly = false,
    showEventTooltips = true,
    showAppointmentTagTooltip = true,
    softSlotDisplayMode = "interactive",
    isLoading = false,
    hiddenEventId = null,
    activeMovePosition = null,
    movePreview = null,
    onMoveStart = () => {},
    allowSlotCreation = true,
    allowBlockerCreation = allowSlotCreation,
    showUpdateSuccessNotification = true,
    showHeader = true,
    formatDayHeader,
    formatAddress,
  }: Props = $props()

  let eventsAreaEl: HTMLDivElement | null = null
  let interaction = $state<InteractionState | null>(null)

  const canDirectlyEditEvents = $derived(
    !readOnly && !isLoading && interactionConfig.canDirectlyEditEvents,
  )
  const nowTs = $derived(now.getTime())
  const columnDayStart = $derived(startOfDay(column.day))
  const todayStart = $derived(startOfDay(now))
  const isPastDay = $derived(columnDayStart.getTime() < todayStart.getTime())
  const totalMinutes = $derived(Math.max(0, (timeSlots.length - 1) * layout.slotMinutes))
  const viewStartMinutes = $derived(layout.dayStartHour * 60)
  const timelineHeight = $derived((totalMinutes / layout.slotMinutes) * layout.slotHeight)
  const isDayOff = $derived(Boolean(column.availability?.dayOff))
  const flexSlotsDisabled = $derived(Number(layout.flexSlotsPerDay ?? 0) <= 0)
  const displayTimedEvents = $derived(
    applyRouteVisualGaps({
      events: column.timedEvents,
      edges: column.routeEdges,
      minGapPx: ROUTE_VISUAL_GAP_PX,
    }),
  )
  const displayColumn = $derived({ ...column, timedEvents: displayTimedEvents })
  const hardEvents = $derived.by(() =>
    displayTimedEvents.filter((item) => item.event.flex_type !== "SOFT"),
  )
  const activeSoftSlotDisplayMode = $derived(isLoading ? "hidden" : softSlotDisplayMode)
  const hardMovePreview = $derived(movePreview?.targetKind === "hard" ? movePreview : null)
  const softMovePreview = $derived(movePreview?.targetKind === "soft" ? movePreview : null)
  const resolveMinutesFromDate = (value: Date, day: Date) =>
    minutesFromDate(value, day, layout.dayStartHour)
  const resolveMinutesFromPointer = (clientY: number, area: HTMLElement | null) =>
    minutesFromPointer({
      clientY,
      area,
      slotHeight: layout.slotHeight,
      slotMinutes: layout.slotMinutes,
      slotCount: timeSlots.length - 1,
    })

  const isPastEvent = (item: BoardEvent) => item.isPast ?? item.start.getTime() < nowTs
  const isPastInteractionLocked = (_item: BoardEvent) => false

  const softSlots = $derived(
    buildSoftSlots({
      column: displayColumn,
      flexSlotsPerDay: layout.flexSlotsPerDay,
      slotMinutes: layout.slotMinutes,
      slotHeight: layout.slotHeight,
      viewStartMinutes,
      viewEndMinutes: viewStartMinutes + totalMinutes,
      nowTs,
      includeUnmatchedSoftEvents: activeSoftSlotDisplayMode !== "hidden",
    }),
  )

  const computePosition = (start: Date, end: Date) => {
    return computeEventPosition({ start, end, day: column.day, layout, totalMinutes })
  }

  const getEventPosition = (item: BoardEvent) => {
    if (
      interaction &&
      interaction.item.event.id === item.event.id &&
      interaction.currentColumnId === column.id
    ) {
      const start = interaction.currentStart ?? item.start
      const end = interaction.currentEnd ?? item.end

      return computePosition(start, end)
    }

    return { top: item.top ?? 0, height: item.height ?? layout.minEventHeight }
  }

  const canMoveEvent = (item: BoardEvent) =>
    interactionConfig.canMoveEvent?.(item) ?? canMoveCalendarEventByDrag(item.event)
  const canResizeEvent = (item: BoardEvent) =>
    interactionConfig.canResizeEvent?.(item) ?? canResizeCalendarEventByDrag(item.event)

  const startResize = (item: BoardEvent, direction: "start" | "end", event: PointerEvent) => {
    if (!canDirectlyEditEvents || interaction || !canResizeEvent(item)) return

    event.preventDefault()
    event.stopPropagation()

    interaction = {
      mode: direction === "start" ? "resize-start" : "resize-end",
      item,
      pointerId: event.pointerId,
      originalStart: item.start,
      originalEnd: item.end,
      currentStart: item.start,
      currentEnd: item.end,
      currentColumnId: column.id,
    }

    window.addEventListener("pointermove", handlePointerMove)
    window.addEventListener("pointerup", finishInteraction)
    window.addEventListener("pointercancel", cancelInteraction)
  }

  const startDrag = (item: BoardEvent, event: PointerEvent) => {
    if (!canDirectlyEditEvents || interaction || !canMoveEvent(item)) return

    event.preventDefault()
    event.stopPropagation()

    onMoveStart({
      item,
      pointer: event,
      column: displayColumn,
      area: eventsAreaEl,
      sourceRect: resolveDragSourceRect(item, event),
      sourceKind: "hard",
    })
  }

  const startSoftDrag = (item: BoardEvent, event: PointerEvent) => {
    if (!canDirectlyEditEvents || interaction || !canMoveEvent(item)) return

    event.preventDefault()
    event.stopPropagation()

    onMoveStart({
      item,
      pointer: event,
      column: displayColumn,
      area: eventsAreaEl,
      sourceRect: resolveDragSourceRect(item, event),
      sourceKind: "soft",
      sourceTimeframeStart: item.event.timeframe_start_at
        ? new Date(item.event.timeframe_start_at)
        : item.start,
      sourceTimeframeEnd: item.event.timeframe_end_at
        ? new Date(item.event.timeframe_end_at)
        : item.end,
    })
  }

  const handlePointerMove = (event: PointerEvent) => {
    if (!interaction) return

    const minutes = resolveMinutesFromPointer(event.clientY, eventsAreaEl)

    if (minutes == null) return

    const nextTimes = computeResizeTimes({
      mode: interaction.mode,
      pointerMinutes: minutes,
      originalStart: interaction.originalStart,
      originalEnd: interaction.originalEnd,
      day: column.day,
      layout,
      totalMinutes,
    })
    interaction.currentStart = nextTimes.start
    interaction.currentEnd = nextTimes.end
  }

  const clearListeners = () => {
    window.removeEventListener("pointermove", handlePointerMove)
    window.removeEventListener("pointerup", finishInteraction)
    window.removeEventListener("pointercancel", cancelInteraction)
  }

  const notifyCalendarChangeSaved = () => {
    newNotification({ class: "success", html: "Kalenderänderung gespeichert." })
  }

  const cancelInteraction = () => {
    clearListeners()
    interaction = null
  }

  const finishInteraction = () => {
    if (!interaction) return

    clearListeners()
    const current = interaction

    const start = current.currentStart ?? current.originalStart
    const end = current.currentEnd ?? current.originalEnd
    const employeeId = column.employee?.id ?? null

    const changed =
      start.getTime() !== current.originalStart.getTime() ||
      end.getTime() !== current.originalEnd.getTime() ||
      employeeId !== (current.item.event.employee_id ?? null)

    if (!changed) {
      interaction = null

      return
    }

    interaction = null

    try {
      const result = actions.updateEvent({
        event: current.item.event,
        start,
        end,
        employeeId,
      })
      void Promise.resolve(result)
        .then((accepted) => {
          if (accepted !== false && showUpdateSuccessNotification) notifyCalendarChangeSaved()
        })
        .catch((err) => {
          console.error("calendar_interaction_update_failed", err)
          newNotification({
            class: "error",
            html: "Änderung konnte nicht übernommen werden. Bitte Seite aktualisieren und erneut versuchen.",
          })
        })
    } catch (err) {
      console.error("calendar_interaction_update_failed", err)
      newNotification({
        class: "error",
        html: "Änderung konnte nicht übernommen werden. Bitte Seite aktualisieren und erneut versuchen.",
      })
    }
  }

  const eventCardFor = (item: BoardEvent, event: PointerEvent) => {
    const target = event.target
    const targetCard =
      target instanceof Element
        ? (target.closest("[data-calendar-event-id]") as HTMLElement | null)
        : null

    if (targetCard?.dataset.calendarEventId === item.event.id) return targetCard

    const cards = eventsAreaEl?.querySelectorAll<HTMLElement>("[data-calendar-event-id]") ?? []

    return Array.from(cards).find((card) => card.dataset.calendarEventId === item.event.id) ?? null
  }

  const resolveDragSourceRect = (item: BoardEvent, event: PointerEvent) => {
    const sourceCard = eventCardFor(item, event)
    const rect = sourceCard?.getBoundingClientRect()
    const fallbackWidth = Math.max(160, eventsAreaEl?.clientWidth ?? 260)
    const fallbackHeight = Math.max(layout.minEventHeight, item.height ?? layout.minEventHeight)
    const width = Math.max(1, rect?.width ?? fallbackWidth)
    const height = Math.max(1, rect?.height ?? fallbackHeight)
    const rawOffsetX = rect ? event.clientX - rect.left : width / 2
    const rawOffsetY = rect ? event.clientY - rect.top : Math.min(height / 2, 28)

    return {
      width,
      height,
      pointerOffsetX: Math.max(0, Math.min(width, rawOffsetX)),
      pointerOffsetY: Math.max(0, Math.min(height, rawOffsetY)),
    }
  }

  onDestroy(() => {
    clearListeners()
  })
</script>

<div
  class="calendar-column"
  class:past-day={isPastDay}
  class:body-divider={showBodyDivider}
  style={`--column-accent:${column.color ?? "var(--kontext-accent)"}`}
>
  {#if showHeader}
    <ColumnHeader
      {column}
      {formatDayHeader}
      showCancelReplan={!readOnly && !isLoading && showCancelReplan}
      {cancelReplanDisabled}
      {isPastDay}
      {actions}
      readOnly={readOnly || isLoading}
      {allowSlotCreation}
      {allowBlockerCreation}
    />
  {/if}

  <div class="column-body">
    <TimeScale {timeSlots} />
    <div
      class="events-area"
      style={`--soft-col-width:${ROUTE_SOFT_COLUMN_WIDTH_PX}px;--route-gutter-width:${ROUTE_GUTTER_WIDTH_PX}px;`}
    >
      <div class="events-wrap">
        <RouteConnectorOverlay
          edges={isLoading ? [] : column.routeEdges}
          events={isLoading ? [] : displayTimedEvents}
          {hiddenEventId}
          viewHeight={timelineHeight}
          scopeId={column.id}
        />
        <SoftSlotRail
          column={displayColumn}
          {softSlots}
          {isDayOff}
          {flexSlotsDisabled}
          {viewStartMinutes}
          viewEndMinutes={viewStartMinutes + totalMinutes}
          {layout}
          {actions}
          {formatAddress}
          {showEventTooltips}
          {showAppointmentTagTooltip}
          displayMode={activeSoftSlotDisplayMode}
          readOnly={readOnly || isLoading}
          {allowSlotCreation}
          {hiddenEventId}
          movePreview={softMovePreview}
          {canDirectlyEditEvents}
          {canMoveEvent}
          onMoveStart={startSoftDrag}
        />
        <div class="route-gutter" aria-hidden="true"></div>
        <div
          class="timeline-area"
          class:loading={isLoading}
          class:off-day={isDayOff}
          class:past-day={isPastDay}
          bind:this={eventsAreaEl}
          data-calendar-area="events"
          data-column-id={column.id}
          data-day-ts={column.day.getTime()}
          data-employee-id={column.employee?.id ?? ""}
        >
          <AvailabilityMask
            availability={column.availability}
            {viewStartMinutes}
            viewEndMinutes={viewStartMinutes + totalMinutes}
            slotMinutes={layout.slotMinutes}
            slotHeight={layout.slotHeight}
            showDayOffLabel={true}
          />
          <TimelineGrid
            columnDay={column.day}
            employeeId={column.employee?.id}
            {timeSlots}
            {layout}
            {nowTs}
            {isPastDay}
            {actions}
            readOnly={readOnly || isLoading}
            {allowSlotCreation}
          />
          <ExternalBusyIntervalLayer intervals={column.externalBusyIntervals ?? []} />
          {#if isLoading}
            <ColumnLoadingOverlay />
          {:else}
            <EventLayer
              {hardEvents}
              minEventHeight={layout.minEventHeight}
              {formatAddress}
              employeeLabel={column.employee?.label ?? ""}
              calendarSyncEnabled={Boolean(column.calendarSyncEnabled)}
              {canDirectlyEditEvents}
              {isPastEvent}
              {isPastInteractionLocked}
              {canMoveEvent}
              {canResizeEvent}
              activeEventId={interaction?.item.event.id ?? null}
              {hiddenEventId}
              {activeMovePosition}
              movePreview={hardMovePreview}
              {getEventPosition}
              {actions}
              readOnly={readOnly || isLoading}
              {showEventTooltips}
              {showAppointmentTagTooltip}
              onDragStart={startDrag}
              onResizeStart={startResize}
            />
          {/if}
        </div>
      </div>
    </div>
  </div>
</div>

<style lang="less">
  @import "@kontextwerk/web-sdk/theme/tokens.less";

  .calendar-column {
    background: var(--cal-bg, #ffffff);
    min-width: var(--column-min, 280px);
    width: 100%;
    flex: 1 1 var(--column-min, 280px);
    display: flex;
    flex-direction: column;
    box-sizing: border-box;

    &.body-divider .column-body {
      border-left: 1px solid var(--cal-border, rgba(0, 0, 0, 0.06));
    }

    .column-body {
      display: grid;
      grid-template-columns: 36px 1fr;
      gap: 0.25rem;
      padding: 0.75rem 0.9rem 0.9rem 0.9rem;
      min-height: calc(var(--time-height) + 8px);
      box-sizing: border-box;

      .events-area {
        min-height: var(--time-height);
        border: 1px solid var(--cal-border, rgba(0, 0, 0, 0.06));
        background: var(--cal-surface, #eceaea);
        overflow: hidden;
        position: relative;

        .events-wrap {
          display: grid;
          grid-template-columns: var(--soft-col-width, 100px) minmax(0, 1fr);
          min-height: var(--time-height);
          position: relative;
        }

        .route-gutter {
          position: absolute;
          inset-block: 0;
          left: var(--soft-col-width, 100px);
          width: var(--route-gutter-width, 18px);
          background: linear-gradient(
            90deg,
            transparent 0,
            color-mix(in srgb, var(--cal-border, rgba(0, 0, 0, 0.08)) 48%, transparent) 50%,
            transparent 100%
          );
          opacity: 0.36;
          pointer-events: none;
          z-index: 0;
        }

        .timeline-area {
          position: relative;
          min-height: var(--time-height);

          &.loading {
            pointer-events: none;
          }
        }

        .timeline-area.off-day {
          background:
            repeating-linear-gradient(
              135deg,
              color-mix(in srgb, var(--cal-accent-soft, #ad514c) 32%, transparent) 0,
              color-mix(in srgb, var(--cal-accent-soft, #ad514c) 32%, transparent) 14px,
              color-mix(in srgb, var(--cal-accent-soft, #ad514c) 18%, transparent) 14px,
              color-mix(in srgb, var(--cal-accent-soft, #ad514c) 18%, transparent) 28px
            ),
            color-mix(in srgb, var(--cal-surface, #eceaea) 55%, transparent);
          background-position: var(--mask-offset, 0px) 0;
        }

        .timeline-area.past-day {
          background: linear-gradient(
            180deg,
            color-mix(in srgb, var(--cal-text, #2f4858) 6%, transparent) 0%,
            color-mix(in srgb, var(--cal-text, #2f4858) 10%, transparent) 100%
          );
          border-style: dashed;
        }
      }
    }
  }
</style>
